import React from 'react';
import './PartsList.css';

const PartsList = () => {
  const sensors = [
    {
      id: 'canopy-sensor',
      name: 'Canopy Systems Multi-Sensor',
      icon: 'ri-sensor-line',
      description: 'All-in-one sensor unit that monitors temperature, humidity, light (lux), and optional water level. This integrated sensor provides comprehensive environmental monitoring in a single device.',
      features: [
        {
          name: 'Temperature Monitoring',
          description: 'Precise ambient temperature measurement'
        },
        {
          name: 'Humidity Monitoring',
          description: 'Relative humidity tracking for optimal growing conditions'
        },
        {
          name: 'Light Intensity (Lux)',
          description: 'Measures light levels to ensure proper plant growth'
        },
        {
          name: 'Water Level (Optional)',
          description: 'Optional water level detection for reservoir monitoring'
        }
      ],
      note: 'This sensor is provided by Canopy Systems and is designed to work seamlessly with our platform.'
    }
  ];

  const actuators = [
    {
      id: 'irrigation',
      name: 'Irrigation System',
      icon: 'ri-water-flash-line',
      description: 'Automated watering system for precise plant hydration',
      recommendations: [
        {
          name: '12V Solenoid Valve',
          specs: '12V DC, 1/2" or 3/4" NPT, normally closed',
          useCase: 'Primary valve for main water line control'
        },
        {
          name: '5V Relay Module',
          specs: '5V/12V compatible, optocoupler isolated',
          useCase: 'Control solenoid valves safely from microcontroller'
        },
        {
          name: 'Drip Irrigation Kit',
          specs: 'Tubing, emitters, connectors, timer-ready',
          useCase: 'Complete system for efficient water delivery'
        },
        {
          name: 'Peristaltic Pump (Optional)',
          specs: '12V DC, adjustable flow rate',
          useCase: 'For nutrient dosing or precise water delivery'
        }
      ]
    },
    {
      id: 'light',
      name: 'Grow Light System',
      icon: 'ri-lightbulb-line',
      description: 'Controlled lighting for indoor plant growth',
      recommendations: [
        {
          name: 'Full Spectrum LED Grow Light',
          specs: '12V/24V, dimmable, 400-700nm spectrum',
          useCase: 'Primary lighting source for indoor gardens'
        },
        {
          name: 'LED Strip Lights (5050 RGB)',
          specs: '12V, RGB, addressable, PWM dimmable',
          useCase: 'Customizable lighting with color control'
        },
        {
          name: 'Relay Module for High-Power Lights',
          specs: '10A/30A relay, AC/DC compatible',
          useCase: 'Control high-wattage grow lights safely'
        },
        {
          name: 'MOSFET Module (PWM Control)',
          specs: 'PWM dimming, 5V/12V compatible',
          useCase: 'Smooth dimming control for LED systems'
        }
      ]
    },
    {
      id: 'fan',
      name: 'Ventilation Fan',
      icon: 'ri-windy-line',
      description: 'Air circulation and temperature control',
      recommendations: [
        {
          name: '12V DC Brushless Fan',
          specs: '12V, PWM speed control, 80-200mm sizes',
          useCase: 'Quiet, efficient cooling for grow spaces'
        },
        {
          name: 'AC Exhaust Fan with Relay',
          specs: '110V/220V AC, high CFM, relay-controlled',
          useCase: 'Powerful ventilation for larger spaces'
        },
        {
          name: '5V/12V Relay Module',
          specs: 'Optocoupler isolated, AC/DC switching',
          useCase: 'Safe control of AC fans from microcontroller'
        }
      ]
    },
    {
      id: 'camera',
      name: 'Camera System',
      icon: 'ri-camera-line',
      description: 'Visual monitoring and time-lapse photography',
      recommendations: [
        {
          name: 'Raspberry Pi Camera Module v2',
          specs: '8MP, 1080p video, CSI interface',
          useCase: 'High-quality imaging for Raspberry Pi setups'
        },
        {
          name: 'ESP32-CAM',
          specs: '2MP, WiFi, OV2640 sensor, low cost',
          useCase: 'Standalone camera module with built-in WiFi'
        },
        {
          name: 'USB Webcam (Logitech C920/C930)',
          specs: '1080p, USB 2.0, auto-focus',
          useCase: 'Plug-and-play option for computer-based systems'
        },
        {
          name: 'Arducam Camera Modules',
          specs: 'Various sensors, SPI/I2C interface',
          useCase: 'Flexible camera options for different microcontrollers'
        }
      ]
    },
    {
      id: 'heating',
      name: 'Temperature Control',
      icon: 'ri-fire-line',
      description: 'Heating elements for temperature regulation',
      recommendations: [
        {
          name: 'Heating Pad (Seedling Mat)',
          specs: '12V/110V, 10-20W, thermostatic control',
          useCase: 'Bottom heat for seed germination and root zones'
        },
        {
          name: 'Ceramic Heat Emitter',
          specs: '110V/220V AC, 50-150W, infrared',
          useCase: 'Ambient heating for larger grow spaces'
        },
        {
          name: 'Relay Module (High Power)',
          specs: '10A/30A, AC/DC compatible',
          useCase: 'Control heating elements safely'
        }
      ]
    },
    {
      id: 'humidifier',
      name: 'Humidity Control',
      icon: 'ri-mist-line',
      description: 'Humidification and dehumidification systems',
      recommendations: [
        {
          name: 'Ultrasonic Humidifier Module',
          specs: '5V/12V, mist output, adjustable',
          useCase: 'Compact humidification for small to medium spaces'
        },
        {
          name: 'AC Humidifier with Relay Control',
          specs: '110V AC, relay-controlled, high output',
          useCase: 'Powerful humidification for larger grow rooms'
        },
        {
          name: 'Dehumidifier with Smart Plug',
          specs: 'WiFi-enabled smart plug, AC control',
          useCase: 'Automated dehumidification based on sensor readings'
        }
      ]
    }
  ];

  return (
    <div className="parts-list-container">
      <div className="parts-list-hero">
        <div className="parts-list-hero-content">
          <h1>Hardware Parts List</h1>
          <p>Complete guide to sensors and actuators for building your Canopy Systems setup</p>
        </div>
      </div>

      <div className="parts-list-content">
        <div className="parts-intro">
          <h2>Getting Started</h2>
          <p>
            This guide provides detailed recommendations for building your Canopy Systems setup. 
            The multi-sensor unit is provided by Canopy Systems and includes all essential environmental monitoring capabilities. 
            For actuators and additional components, choose options based on your specific needs, budget, and scale of operation.
          </p>
        </div>

        {/* Sensors Section */}
        <section className="parts-section">
          <div className="section-header">
            <i className="ri-sensor-line"></i>
            <h2>Sensors</h2>
            <p className="section-description">
              The Canopy Systems multi-sensor provides comprehensive environmental monitoring in a single integrated unit. 
              This sensor collects all the essential data you need to make informed decisions about your plants.
            </p>
          </div>

          <div className="parts-grid">
            {sensors.map((sensor) => (
              <div key={sensor.id} className="part-card sensor-card">
                <div className="part-card-header">
                  <i className={sensor.icon}></i>
                  <h3>{sensor.name}</h3>
                </div>
                <p className="part-description">{sensor.description}</p>
                <div className="recommendations">
                  <h4>Sensor Features:</h4>
                  <ul className="recommendations-list">
                    {sensor.features.map((feature, index) => (
                      <li key={index} className="recommendation-item">
                        <div className="recommendation-name">{feature.name}</div>
                        <div className="recommendation-usecase">{feature.description}</div>
                      </li>
                    ))}
                  </ul>
                  {sensor.note && (
                    <div className="sensor-note">
                      <i className="ri-information-line"></i>
                      <p>{sensor.note}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Actuators Section */}
        <section className="parts-section">
          <div className="section-header">
            <i className="ri-tools-line"></i>
            <h2>Actuators</h2>
            <p className="section-description">
              Actuators are devices that perform actions based on sensor data. They allow you to automate 
              irrigation, lighting, ventilation, and other environmental controls.
            </p>
          </div>

          <div className="parts-grid">
            {actuators.map((actuator) => (
              <div key={actuator.id} className="part-card actuator-card">
                <div className="part-card-header">
                  <i className={actuator.icon}></i>
                  <h3>{actuator.name}</h3>
                </div>
                <p className="part-description">{actuator.description}</p>
                <div className="recommendations">
                  <h4>Recommended Components:</h4>
                  <ul className="recommendations-list">
                    {actuator.recommendations.map((rec, index) => (
                      <li key={index} className="recommendation-item">
                        <div className="recommendation-name">{rec.name}</div>
                        <div className="recommendation-specs">{rec.specs}</div>
                        <div className="recommendation-usecase">{rec.useCase}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Additional Components Section */}
        <section className="parts-section">
          <div className="section-header">
            <i className="ri-settings-3-line"></i>
            <h2>Additional Components</h2>
            <p className="section-description">
              Essential supporting hardware for building a complete IoT system.
            </p>
          </div>

          <div className="parts-grid">
            <div className="part-card additional-card">
              <div className="part-card-header">
                <i className="ri-cpu-line"></i>
                <h3>Microcontrollers</h3>
              </div>
              <p className="part-description">Processing units that connect sensors and actuators to the platform</p>
              <div className="recommendations">
                <h4>Recommended Options:</h4>
                <ul className="recommendations-list">
                  <li className="recommendation-item">
                    <div className="recommendation-name">ESP32 Development Board</div>
                    <div className="recommendation-specs">WiFi & Bluetooth, dual-core, low power</div>
                    <div className="recommendation-usecase">Best for wireless sensor nodes and actuators</div>
                  </li>
                  <li className="recommendation-item">
                    <div className="recommendation-name">Raspberry Pi 4 Model B</div>
                    <div className="recommendation-specs">4GB/8GB RAM, WiFi, GPIO pins, Linux OS</div>
                    <div className="recommendation-usecase">Ideal for camera systems and complex setups</div>
                  </li>
                  <li className="recommendation-item">
                    <div className="recommendation-name">Arduino Uno/Nano</div>
                    <div className="recommendation-specs">5V, 16MHz, simple and reliable</div>
                    <div className="recommendation-usecase">Great for learning and simple automation projects</div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="part-card additional-card">
              <div className="part-card-header">
                <i className="ri-plug-line"></i>
                <h3>Power & Connectivity</h3>
              </div>
              <p className="part-description">Essential components for powering and connecting your devices</p>
              <div className="recommendations">
                <h4>Recommended Options:</h4>
                <ul className="recommendations-list">
                  <li className="recommendation-item">
                    <div className="recommendation-name">12V Power Supply (5A+)</div>
                    <div className="recommendation-specs">DC adapter, multiple outputs, overcurrent protection</div>
                    <div className="recommendation-usecase">Power source for relays, solenoids, and fans</div>
                  </li>
                  <li className="recommendation-item">
                    <div className="recommendation-name">USB Power Supply (5V, 2A+)</div>
                    <div className="recommendation-specs">USB-A or USB-C, fast charging capable</div>
                    <div className="recommendation-usecase">Power for microcontrollers and low-power sensors</div>
                  </li>
                  <li className="recommendation-item">
                    <div className="recommendation-name">Breadboard & Jumper Wires</div>
                    <div className="recommendation-specs">830 tie-points, male-to-male/female wires</div>
                    <div className="recommendation-usecase">Prototyping and connecting components</div>
                  </li>
                  <li className="recommendation-item">
                    <div className="recommendation-name">Dupont Connectors & Wire</div>
                    <div className="recommendation-specs">22-26 AWG, various connector types</div>
                    <div className="recommendation-usecase">Custom wiring for permanent installations</div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="part-card additional-card">
              <div className="part-card-header">
                <i className="ri-shield-check-line"></i>
                <h3>Safety & Protection</h3>
              </div>
              <p className="part-description">Protect your equipment and ensure safe operation</p>
              <div className="recommendations">
                <h4>Recommended Options:</h4>
                <ul className="recommendations-list">
                  <li className="recommendation-item">
                    <div className="recommendation-name">Fuse Holders & Fuses</div>
                    <div className="recommendation-specs">Fast-blow, various amperage ratings</div>
                    <div className="recommendation-usecase">Overcurrent protection for power lines</div>
                  </li>
                  <li className="recommendation-item">
                    <div className="recommendation-name">Waterproof Enclosures</div>
                    <div className="recommendation-specs">IP65/IP67 rated, various sizes</div>
                    <div className="recommendation-usecase">Protect electronics in humid or outdoor environments</div>
                  </li>
                  <li className="recommendation-item">
                    <div className="recommendation-name">Surge Protector</div>
                    <div className="recommendation-specs">AC outlet strip, surge suppression</div>
                    <div className="recommendation-usecase">Protect AC-powered devices from voltage spikes</div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Getting Started CTA */}
        <div className="parts-cta">
          <h2>Ready to Build Your System?</h2>
          <p>Join the Canopy Systems platform and start monitoring and automating your growing environment today.</p>
          <a href="/contact" className="cta-button">Get Started</a>
        </div>
      </div>
    </div>
  );
};

export default PartsList;

